import { makeStyles, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { CustomQuestion } from '../../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../../api/CustomQuestion/types/Type';
import { ConsentType } from '../../api/auth';
import { EnrichedMentorResponse } from '../../api/enrich';
import { TenantId } from '../../api/tenants/Tenant';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { formatDateToRFC } from '../../utils/date';
import {
  BUSINESS_MENTOR_WIZARD,
  FUNDRAISING_MENTOR_WIZARD,
  GENDER_MENTOR,
  lengthField,
  MARKETING_MENTOR_WIZARD,
  RACE,
  RADIO_SELECT,
  SALES_MENTOR_WIZARD,
  SENIORITY_OPTIONS,
} from '../../utils/form';
import { checkSizeTablet, createMarkup } from '../../utils/functions';
import { CheckboxListOther, FormGroup, Text, UserLogo, StepHeader } from '../common';
import { Attachment, getAttachmentFromFile } from '../common/attachment-card';
import PrefillModal from '../common/prefill-modal';
import Wizard, { FormValues } from '../common/wizard';
import CustomFormField from './custom-form/custom-form-field';
import {
  FormCheckbox,
  FormComboBox,
  FormDateInput,
  FormSelect,
  FormSpecializationInput,
  NumberField,
  TextFieldWrapper,
} from './wrappers';

const useStyles = makeStyles((theme) => ({
  multilineField: {
    minHeight: 64,
  },
  title: {
    marginBottom: 32,
  },
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  intro: {
    display: 'block',
    marginBottom: 20,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  consent: {
    marginBottom: 20,
  },
  checkboxLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
    },
  },
  aboutField: {
    minHeight: 64,
  },
}));

interface Props {
  onSubmit: (values: FormValues) => void;
  tenantId: string;
  isLoading: boolean;
  consents: ConsentType[];
  showCustomAdditionalInfo?: boolean;
  additionalInfoCustomQuestions?: CustomQuestion[] | null;
  intro: string;
  logoBlob: string;
  setLogoBlob: (blob: string) => void;
  attachments: Map<string, Attachment>;
  setAttachments: React.Dispatch<React.SetStateAction<Map<string, Attachment>>>;
  files: Map<string, File>;
  setFiles: React.Dispatch<React.SetStateAction<Map<string, File>>>;
  mentorCommitmentEnabled?: boolean;
  showPrefillModal?: boolean;
  onPrefillComplete?: (data: EnrichedMentorResponse) => void;
  onPrefillDismiss?: () => void;
  prefillData?: Partial<FormValues> | null;
}

interface StepProps {
  countSteps: number;
  stepNumber: number;
}

interface Step1Props extends StepProps {
  intro: string;
  logoBlob: string;
  tenantId: TenantId;
  setLogoBlob: (blob: string) => void;
}

const Step1 = ({
  countSteps,
  stepNumber,
  intro,
  logoBlob,
  tenantId,
  setLogoBlob,
}: Step1Props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { rb } = useResourceBundles();

  const validateLogoFile = (logo: File) => {
    const fileName = logo.name;
    if (fileName.length > lengthField.logoFileName) {
      enqueueSnackbar('The maximum length of the file name is 75 characters', {
        variant: 'error',
      });
      return false;
    }
    return true;
  };

  return (
    <div>
      {intro && (
        <Text variant='normal' className={classes.intro}>
          {intro}
        </Text>
      )}
      <StepHeader
        title='General Information'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      <div className={classes.sectionBlock}>
        <FormGroup>
          <Field<string>
            name='file'
            parse={(value) => (!value ? '' : value)}
            component={({ input }) => (
              <UserLogo
                logo={logoBlob}
                name={' '}
                onUpload={(photoFile) => {
                  if (validateLogoFile(photoFile)) {
                    const blobFile = URL.createObjectURL(photoFile);
                    setLogoBlob(blobFile);
                    input.onChange(photoFile);
                  }
                }}
                onRemove={() => {
                  setLogoBlob('');
                  input.onChange(null);
                }}
                showLabelIfEmpty
              />
            )}
          />
        </FormGroup>
        <FormGroup mobile={checkSizeTablet(800)}>
          <Field<string>
            name='firstName'
            component={TextFieldWrapper}
            label='First name*'
            testid='mentor-wizard-step1-firstName'
            InputProps={{
              inputProps: {
                maxLength: lengthField.firstName,
              },
            }}
          />
          <Field<string>
            name='lastName'
            component={TextFieldWrapper}
            testid='mentor-wizard-step1-lastName'
            label='Last name*'
            InputProps={{
              inputProps: {
                maxLength: lengthField.lastName,
              },
            }}
          />
        </FormGroup>
        <FormGroup mobile={checkSizeTablet(800)}>
          <Field<string>
            name='email'
            component={TextFieldWrapper}
            testid='mentor-wizard-step1-email'
            label='Email*'
            formatOnBlur
            format={(value: string) => {
              return value ? value.toLowerCase() : value;
            }}
          />
          <Field<string>
            name='emailConfirm'
            component={TextFieldWrapper}
            label='Email (confirm)*'
            testid='mentor-wizard-step1-emailConfirm'
            formatOnBlur
            format={(value: string) => {
              return value ? value.toLowerCase() : value;
            }}
          />
        </FormGroup>
        <FormGroup mobile={checkSizeTablet(800)}>
          <Field<string>
            name='dateOfBirth'
            component={FormDateInput}
            label='Date of birth'
            testid='mentor-wizard-step1-dateOfBirth'
            editable
            parse={(value) => {
              try {
                if (value) {
                  return formatDateToRFC(value);
                }
                return '';
              } catch (e: unknown) {
                return 'invalid';
              }
            }}
          />
          <Field<string>
            name='linkedInProfile'
            component={TextFieldWrapper}
            label='LinkedIn profile (link)'
            placeholder='https://www.linkedin.com/in/your-profile'
            testid='mentor-wizard-step1-linkedInProfile'
          />
        </FormGroup>
        <FormGroup mobile={checkSizeTablet(800)}>
          <Field<string>
            name='gender'
            testid='mentor-wizard-step1-gender'
            component={FormSelect}
            label='Gender'
            options={GENDER_MENTOR}
          />
          <Field<string>
            name='race'
            testid='mentor-wizard-step1-race'
            component={FormSelect}
            label='Race'
            options={RACE}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='phone'
            label='Phone*'
            component={TextFieldWrapper}
            testid='mentor-wizard-step1-phone'
          />
        </FormGroup>

        <FormGroup>
          <Field
            name='businessIndustries'
            component={FormSpecializationInput}
            label='Specialization*'
            data-testid='mentor-wizard-step1-businessIndustries'
            tenantId={tenantId}
          />
        </FormGroup>

        <FormGroup>
          <Field<string>
            testid='mentor-wizard-step1-about'
            name='about'
            component={TextFieldWrapper}
            label='About*'
            multiline
            InputProps={{
              inputProps: {
                className: classes.aboutField,
                maxLength: 512,
              },
            }}
          />
        </FormGroup>

        <FormGroup>
          <Field<string>
            name='currentRole'
            component={TextFieldWrapper}
            label='Current Role'
            testid='mentor-wizard-step1-currentRole'
            InputProps={{
              inputProps: {
                maxLength: lengthField.currentRole,
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='companyName'
            component={TextFieldWrapper}
            label='Company Name'
            testid='mentor-wizard-step1-companyName'
            InputProps={{
              inputProps: {
                maxLength: lengthField.companyName,
              },
            }}
          />
        </FormGroup>
        <FormGroup mobile={checkSizeTablet(800)}>
          <Field<string>
            name='seniority'
            testid='mentor-wizard-step1-seniority'
            component={FormComboBox}
            label='Seniority'
            options={[...SENIORITY_OPTIONS]}
          />
          <Field<string>
            name='experienceYears'
            testid='mentor-wizard-step1-experienceYears'
            component={NumberField}
            label='Experience (years)'
            InputProps={{
              inputProps: {
                min: 0,
                max: 99,
              },
            }}
          />
        </FormGroup>
        <FormGroup mobile={checkSizeTablet(800)}>
          <Field<string>
            name='isEnterpreneur'
            testid='mentor-wizard-step1-isEnterpreneur'
            component={FormSelect}
            label='Entrepreneur'
            options={RADIO_SELECT}
          />
          <Field<string>
            name='isInvestor'
            testid='mentor-wizard-step1-isInvestor'
            component={FormSelect}
            label='Investor'
            options={RADIO_SELECT}
          />
          <Field<string>
            name='mentorCapacity'
            testid='mentor-wizard-step1-mentorCapacity'
            component={NumberField}
            label={`${rb('mentor-u')} Capacity*`}
            InputProps={{
              inputProps: {
                min: 0,
                max: 10,
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='skills'
            testid='mentor-wizard-step1-skills'
            component={TextFieldWrapper}
            label='Skills'
            multiline
            InputProps={{
              inputProps: {
                maxLength: lengthField.skills,
              },
            }}
          />
        </FormGroup>
      </div>
      <div className={classes.sectionBlock}>
        <Typography variant='h3' className={classes.title}>
          Address
        </Typography>
        <FormGroup>
          <Field<string>
            name='address'
            component={TextFieldWrapper}
            label='Address'
            testid='mentor-wizard-step1-address'
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='city'
            component={TextFieldWrapper}
            label='City'
            testid='mentor-wizard-step1-city'
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='state'
            component={TextFieldWrapper}
            label='State'
            testid='mentor-wizard-step1-state'
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='zip'
            component={TextFieldWrapper}
            label='Zip code'
            testid='mentor-wizard-step1-zip'
            InputProps={{
              inputProps: {
                maxLength: lengthField.zip,
              },
            }}
          />
        </FormGroup>
        <FormGroup>
          <Field<string>
            name='country'
            component={TextFieldWrapper}
            label='Country'
            testid='mentor-wizard-step1-country'
          />
        </FormGroup>
      </div>
    </div>
  );
};

interface Step2Props extends StepProps {
  showCustomAdditionalInfo?: boolean;
  additionalInfoCustomQuestions?: CustomQuestion[] | null;
  attachments: Map<string, Attachment>;
  setAttachments: React.Dispatch<React.SetStateAction<Map<string, Attachment>>>;
  files: Map<string, File>;
  setFiles: React.Dispatch<React.SetStateAction<Map<string, File>>>;
  tenantId: TenantId;
}

const Step2 = ({
  countSteps,
  stepNumber,
  showCustomAdditionalInfo,
  additionalInfoCustomQuestions,
  attachments,
  setAttachments,
  files,
  setFiles,
  tenantId,
}: Step2Props) => {
  const classes = useStyles();
  const { rb } = useResourceBundles();

  const formAPI = useForm();
  const stateForm = formAPI.getState();
  const handleChangeField = (onChange: any, name: string, value: string) => {
    onChange(name, value);
  };

  // Subscribe to form state changes to get updated errors and submitFailed
  const [formState, setFormState] = useState(stateForm);
  useEffect(() => {
    const unsubscribe = formAPI.subscribe(
      (state) => {
        setFormState(state);
      },
      { errors: true, submitFailed: true },
    );
    return unsubscribe;
  }, [formAPI]);

  // Get errors and submitFailed from form state for validation display
  const formErrors = formState.errors || {};
  const submitFailed = formState.submitFailed || false;

  const handleUploadFileForQuestion = async (
    questionId: string,
    file: File,
  ): Promise<string> => {
    // Store file locally - will be uploaded after mentor is created
    const attachment = getAttachmentFromFile(file);
    // Create temporary URL from file blob to show file as "uploaded" (no loading)
    const tempUrl = URL.createObjectURL(file);
    attachment.url = tempUrl;

    setFiles((prev) => {
      const newMap = new Map(prev);
      newMap.set(questionId, file);
      return newMap;
    });
    setAttachments((prev) => {
      const newMap = new Map(prev);
      newMap.set(questionId, attachment);
      return newMap;
    });
    // Return temporary marker to indicate file is selected (for validation)
    // Will be replaced with actual filePath after upload
    const tempMarker = `pending:${questionId}`;
    formAPI.change(questionId, tempMarker);
    return tempMarker;
  };

  const handleFileRemove = (questionId: string) => {
    // Clean up temporary blob URL if exists
    const attachment = attachments.get(questionId);
    if (attachment?.url && attachment.url.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.url);
    }

    setFiles((prev) => {
      const newMap = new Map(prev);
      newMap.delete(questionId);
      return newMap;
    });
    setAttachments((prev) => {
      const newMap = new Map(prev);
      newMap.delete(questionId);
      return newMap;
    });
    formAPI.change(questionId, '');
  };

  return (
    <div>
      <StepHeader
        title='Additional Info'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      {showCustomAdditionalInfo && additionalInfoCustomQuestions?.length ? (
        additionalInfoCustomQuestions.map((question) => (
          <CustomFormField
            key={question.id}
            question={question}
            hasAccessToUpdate={true}
            onChange={(value) => formAPI.change(question.id, value)}
            value={formState.values?.[question.id] || ''}
            onUploadFile={
              question.type === CustomQuestionType.ATTACHMENT
                ? (file: File) => handleUploadFileForQuestion(question.id, file)
                : undefined
            }
            onFileRemove={
              question.type === CustomQuestionType.ATTACHMENT
                ? () => handleFileRemove(question.id)
                : undefined
            }
            attachment={
              question.type === CustomQuestionType.ATTACHMENT
                ? attachments.get(question.id)
                : undefined
            }
            error={formErrors[question.id]}
            submitFailed={submitFailed}
          />
        ))
      ) : (
        <>
          <FormGroup>
            <Field<string>
              name='whyBecomeMentor'
              component={TextFieldWrapper}
              label={`Why would you like to become a ${rb('mentor')}?`}
              testid='mentor-wizard-step2-whyBecomeMentor'
              multiline
              InputProps={{
                inputProps: {
                  className: classes.multilineField,
                  maxLength: lengthField.whyBecomeMentor,
                },
              }}
            />
          </FormGroup>

          <CheckboxListOther
            name='salesChallenges'
            options={SALES_MENTOR_WIZARD}
            onChange={(value) =>
              handleChangeField(formAPI.change, 'salesChallenges', value)
            }
            text='What of the following SALES challenges would be within your area of expertise? (select 1-3 that apply best)*'
            labelOther='Other'
            value={stateForm.values.salesChallenges}
            testid='mentor-wizard-step2-salesChallenges'
          />

          <CheckboxListOther
            name='marketingChallenges'
            options={MARKETING_MENTOR_WIZARD}
            onChange={(value) =>
              handleChangeField(formAPI.change, 'marketingChallenges', value)
            }
            text='What of the following MARKETING challenges would be within your area of expertise? (select 1-3 that apply best)*'
            labelOther='Other'
            value={stateForm.values.marketingChallenges}
            testid='mentor-wizard-step2-marketingChallenges'
          />

          <CheckboxListOther
            name='fundraisingChallenges'
            options={FUNDRAISING_MENTOR_WIZARD}
            onChange={(value) =>
              handleChangeField(formAPI.change, 'fundraisingChallenges', value)
            }
            text='What of the following FUNDRASING challenges would be within your area of expertise? (select 1-3 that apply best)*'
            labelOther='Other'
            value={stateForm.values.fundraisingChallenges}
            testid='mentor-wizard-step2-fundraisingChallenges'
          />

          <CheckboxListOther
            name='businessChallenges'
            options={BUSINESS_MENTOR_WIZARD}
            onChange={(value) =>
              handleChangeField(formAPI.change, 'businessChallenges', value)
            }
            text='What of the following BUSINESS challenges would be within your area of expertise? (select 1-3 that apply best)*'
            labelOther='Other'
            value={stateForm.values.businessChallenges}
            testid='mentor-wizard-step2-businessChallenges'
          />
        </>
      )}
    </div>
  );
};

interface Step3Props extends StepProps {
  consents: ConsentType[];
}

const Step3 = ({ countSteps, stepNumber, consents }: Step3Props) => {
  const classes = useStyles();

  return (
    <div>
      <StepHeader
        title='Commitment'
        stepNumber={stepNumber}
        totalSteps={countSteps}
      />
      {consents.map((consent) => (
        <div className={classes.consent}>
          <div
            className={classes.consent}
            dangerouslySetInnerHTML={createMarkup(consent.body)}
          />
          <Field<string>
            name={`field-${consent.id}`}
            component={FormCheckbox}
            label='I accept'
            classNameLabel={classes.checkboxLabel}
            testid='mentor-wizard-step3-accept'
          />
        </div>
      ))}
    </div>
  );
};

function MentorsApplicationsWizardForm({
  onSubmit,
  tenantId,
  isLoading,
  consents,
  showCustomAdditionalInfo,
  additionalInfoCustomQuestions,
  intro,
  logoBlob,
  setLogoBlob,
  attachments,
  setAttachments,
  files,
  setFiles,
  mentorCommitmentEnabled = false,
  showPrefillModal = false,
  onPrefillComplete,
  onPrefillDismiss,
  prefillData,
}: Props) {
  const withConsents = consents.length > 0 && mentorCommitmentEnabled;
  const countSteps = withConsents ? 3 : 2;

  const initialValues = useMemo(
    () =>
      prefillData
        ? {
            businessIndustries: [],
            mentorCapacity: '2',
            ...prefillData,
          }
        : undefined,
    [prefillData],
  );

  return (
    <>
      {showPrefillModal && onPrefillComplete && onPrefillDismiss && (
        <PrefillModal
          open={showPrefillModal}
          onClose={onPrefillDismiss}
          onPrefillComplete={onPrefillComplete}
          tenantId={tenantId as TenantId}
          wizardType='mentor'
        />
      )}
      <Wizard
        consents={consents}
        onSubmit={onSubmit}
        tenantId={tenantId}
        showCustomAdditionalInfo={showCustomAdditionalInfo}
        additionalInfoCustomQuestions={additionalInfoCustomQuestions}
        isLoading={isLoading}
        mentorCommitmentEnabled={mentorCommitmentEnabled}
        initialValues={initialValues}
      >
        <Step1
          countSteps={countSteps}
          stepNumber={1}
          intro={intro}
          logoBlob={logoBlob}
          setLogoBlob={setLogoBlob}
          tenantId={tenantId as TenantId}
        />
        <Step2
          countSteps={countSteps}
          stepNumber={2}
          showCustomAdditionalInfo={showCustomAdditionalInfo}
          additionalInfoCustomQuestions={additionalInfoCustomQuestions}
          attachments={attachments}
          setAttachments={setAttachments}
          files={files}
          setFiles={setFiles}
          tenantId={tenantId as TenantId}
        />
        {withConsents && (
          <Step3 countSteps={countSteps} stepNumber={3} consents={consents} />
        )}
      </Wizard>
    </>
  );
}

export default MentorsApplicationsWizardForm;
